import React, { useState } from 'react'
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Typography,
  Paper,
  CircularProgress,
} from '@mui/material'
import { BugReport, Feedback as FeedbackIcon } from '@mui/icons-material'
import { useSubmitFeedbackMutation } from '@store/api/feedbackApi'

interface FeedbackFormProps {
  onSuccess?: (referenceNumber: string) => void
}

const CATEGORIES = [
  { value: 'bug_report', label: 'Bug Report' },
  { value: 'feature_request', label: 'Feature Request' },
  { value: 'usability', label: 'Usability' },
  { value: 'performance', label: 'Performance' },
  { value: 'other', label: 'Other' },
]

const SEVERITIES = [
  { value: 'low', label: 'Low - Minor issue, workaround available' },
  { value: 'medium', label: 'Medium - Affects functionality' },
  { value: 'high', label: 'High - Major feature broken' },
  { value: 'critical', label: 'Critical - App unusable' },
]

const FeedbackForm: React.FC<FeedbackFormProps> = ({ onSuccess }) => {
  const [type, setType] = useState<'bug' | 'feedback'>('feedback')
  const [subject, setSubject] = useState('')
  const [description, setDescription] = useState('')
  const [severity, setSeverity] = useState('medium')
  const [category, setCategory] = useState('')
  const [error, setError] = useState('')
  const [successRef, setSuccessRef] = useState('')

  const [submitFeedback, { isLoading }] = useSubmitFeedbackMutation()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!subject.trim() || !description.trim()) {
      setError('Subject and description are required')
      return
    }

    try {
      const result = await submitFeedback({
        type,
        subject: subject.trim(),
        description: description.trim(),
        ...(type === 'bug' && { severity }),
        ...(category && { category }),
      }).unwrap()

      setSuccessRef(result.referenceNumber)
      setSubject('')
      setDescription('')
      setSeverity('medium')
      setCategory('')
      onSuccess?.(result.referenceNumber)
    } catch (err: any) {
      setError(err?.data?.message || 'Failed to submit. Please try again.')
    }
  }

  if (successRef) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="success" sx={{ mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Submitted successfully!
          </Typography>
          <Typography variant="body2">
            Your tracking reference number is: <strong>{successRef}</strong>
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            You can track the status of your submission in Profile → My Reports.
          </Typography>
        </Alert>
        <Button variant="outlined" onClick={() => setSuccessRef('')}>
          Submit another report
        </Button>
      </Paper>
    )
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box component="form" onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Button
            variant={type === 'feedback' ? 'contained' : 'outlined'}
            startIcon={<FeedbackIcon />}
            onClick={() => setType('feedback')}
            type="button"
          >
            Feedback
          </Button>
          <Button
            variant={type === 'bug' ? 'contained' : 'outlined'}
            startIcon={<BugReport />}
            onClick={() => setType('bug')}
            color={type === 'bug' ? 'error' : 'primary'}
            type="button"
          >
            Bug Report
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <TextField
          fullWidth
          label="Subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          required
          sx={{ mb: 2 }}
          placeholder="Brief summary of your report"
        />

        <TextField
          fullWidth
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          required
          multiline
          rows={4}
          sx={{ mb: 2 }}
          placeholder={type === 'bug' ? 'Describe the bug, steps to reproduce, and expected behavior' : 'Describe your feedback or suggestion'}
        />

        {type === 'bug' && (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Severity</InputLabel>
            <Select value={severity} onChange={(e) => setSeverity(e.target.value)} label="Severity">
              {SEVERITIES.map((s) => (
                <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Category (optional)</InputLabel>
          <Select value={category} onChange={(e) => setCategory(e.target.value)} label="Category (optional)">
            <MenuItem value="">None</MenuItem>
            {CATEGORIES.map((c) => (
              <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          type="submit"
          variant="contained"
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={20} /> : undefined}
        >
          {isLoading ? 'Submitting...' : 'Submit Report'}
        </Button>
      </Box>
    </Paper>
  )
}

export default FeedbackForm
